import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useAttachmentDataInfinite } from "@/entities/attachments/hooks";
import { DreamerAttachmentFilterMetadata } from "@/entities/attachments/types";
import { ReportPreviewRenderer } from "@/components/global/Chat/components/dreamerReports/ReportPreviewRenderer";
import { ReportRunStateModal } from "@/components/global/Chat/components/dreamerReports/ReportRunStateModal";
import {
  useReportRun,
  useReportRuns
} from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { ReportPreviewTarget } from "@/entities/savedReports/types";
import { useHeldWhileClosing } from "@/hooks/useHeldWhileClosing";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

interface ReportPreviewHostProps {
  /** The report to show, or null once the user has closed it. */
  report: ReportPreviewTarget | null;
  onClose: () => void;
}

interface ShownResult {
  reportId: string;
  attachmentId: string;
  metadata: DreamerAttachmentFilterMetadata;
}

/**
 * Resolves a report the user opened from a list, which is almost never in the
 * loaded chat messages: the lists are paginated over the user's whole history,
 * the socket only carries a recent window, and messaging exposes no
 * single-message read. Both the reports list and the recurring schedules list
 * open a past delivery through here for that reason.
 *
 * The metadata a renderer needs comes back on the attachment filter response,
 * so an attachment id is enough to resolve it. Called with no options on
 * purpose — `useDreamerAttachmentData` inside the renderer uses the hook's
 * defaults, and `size` is part of the query key, so any option passed here
 * would split the cache and cost a second round trip instead of saving one.
 *
 * A report this tab is generating shows its run instead: a generating or
 * failed view while there is no result, and the run's own result once it
 * arrives. A report with no run shows the result it was saved with.
 *
 * The host stays mounted across a close, since the renderer's dialog lives
 * through an exit animation.
 */
export const ReportPreviewHost: React.FC<ReportPreviewHostProps> = ({
  report,
  onClose
}) => {
  const { t } = useTranslation();
  const [reportToRender, clearHeldReport] = useHeldWhileClosing(report);
  const run = useReportRun(reportToRender?.id);
  const { setReportModalOpen } = useReportRuns();

  const isRunFailed =
    run?.status === "failed" ||
    (run?.status === "success" && !run.attachmentId);
  // A run's result replaces the saved one; until it has one there is nothing
  // to fetch.
  const attachmentIdToShow = run
    ? run.status === "success"
      ? run.attachmentId
      : null
    : reportToRender?.originAttachmentId;

  const { metadata, error } = useAttachmentDataInfinite(
    attachmentIdToShow ?? ""
  );

  // The renderer is kept on the last result it showed while a new run
  // generates, so its dialog closes rather than being torn down while open —
  // which would leave the page unclickable.
  const [heldResult, setHeldResult] = useState<ShownResult | null>(null);
  const currentResult: ShownResult | null =
    reportToRender && attachmentIdToShow && metadata
      ? {
          reportId: reportToRender.id,
          attachmentId: attachmentIdToShow,
          metadata
        }
      : null;
  if (
    currentResult &&
    (heldResult?.reportId !== currentResult.reportId ||
      heldResult.attachmentId !== currentResult.attachmentId)
  ) {
    setHeldResult(currentResult);
  }
  if (!reportToRender && heldResult) setHeldResult(null);
  const result =
    currentResult ??
    (reportToRender && heldResult?.reportId === reportToRender.id
      ? heldResult
      : null);

  const showsRunState = !!run && (run.status !== "success" || !metadata);

  // A finished run shown in front of the user needs no notification.
  const shownReportId = report?.id;
  useEffect(() => {
    if (!shownReportId) return;
    setReportModalOpen(shownReportId, true);
    return () => setReportModalOpen(shownReportId, false);
  }, [shownReportId, setReportModalOpen]);

  // The host renders hidden, so a failed fetch would otherwise leave the row
  // looking simply unresponsive. Hand the tab back to the list and say why.
  //
  // The report is dropped rather than held for the closing animation: nothing
  // was ever shown, and holding it would keep this effect on the same failed
  // query, so reopening the row would change no dependency and do nothing.
  // A row carrying no attachment id never enables the query, so it would
  // produce neither metadata nor an error — the same dead click, without even
  // a failed fetch to report. Treat it as the open failure it is. A report
  // that is generating has its result still to come, so it is not one.
  const hasNothingToFetch =
    !!reportToRender && !run && !reportToRender.originAttachmentId;

  useEffect(() => {
    if (!error && !hasNothingToFetch) return;
    toast.error(t(`${TRANSLATION_KEY}.openFailed`));
    clearHeldReport();
    onClose();
  }, [error, hasNothingToFetch, onClose, clearHeldReport, t]);

  if (!reportToRender) return null;

  return (
    <>
      {run && (
        <ReportRunStateModal
          report={reportToRender}
          isOpen={!!report && showsRunState}
          isFailed={isRunFailed}
          onClose={onClose}
        />
      )}
      {result && (
        <ReportPreviewRenderer
          key={reportToRender.id}
          metadata={{
            // The renderer resolves its rows from the metadata's own attachment
            // id, and producers only set that field when they package a chat
            // response. The host already knows the id that fetched this
            // metadata.
            ...result.metadata,
            // `||` not `??`: this module family uses both `null` and `""` as the
            // "no attachment" sentinel, and only the host's id is known good.
            attachmentId: result.metadata.attachmentId || result.attachmentId,
            // The attachment keeps the title the answer was generated with, so
            // a rename only shows here if the row's own name wins.
            title: reportToRender.name || result.metadata.title,
            // A run's result was generated when the run completed.
            generatedAt:
              (run?.status === "success" &&
                run.attachmentId === result.attachmentId &&
                run.completedAt) ||
              result.metadata.generatedAt
          }}
          messageId={reportToRender.originMessageId}
          // A report and its schedule are one row, so the row's id is also its
          // schedule id — but only once it has a cadence. Handing it over for a
          // saved report that carries none loads that row as a schedule, and
          // the modal opens in edit mode against a null cadence and throws.
          scheduleId={reportToRender.isScheduled ? reportToRender.id : null}
          savedReportId={reportToRender.id}
          isReplayable={reportToRender.isReplayable}
          isExpanded={!!report && !showsRunState}
          onClose={onClose}
        />
      )}
    </>
  );
};
